import React from "react";
import {ES, O, RunInAction, RunInAction_Set} from "web-vcore";
import {MapObject, ShapeType} from "../../../../../Server/DB/mapObjects/@MapObject.js";
import {store} from "../../../../../Store/index.js";
import {observer_mgl_r3} from "../../../../../Utils/ThreeJS/General.js";
import {MapTool, Tool, ToolData, ToolInterceptSubresult} from "./@Tool.js";
import {Clone, E, GetEntries} from "js-vextensions";
import {CheckBox, Text, Select, Spinner, Button} from "react-vcomponents";
import {MapDragInfo} from "./Helpers/DragHelper.js";
import {Rect, Vec2, Vec3} from "../../../../../Utils/ThreeJS/VectorStructs.js";
import {GetMapState} from "../../../../../Store/main/maps/$mapState.js";
import {GetOpenMapID} from "../../../../../Store/main/maps.js";
import {GetMapObject, GetMapObjects} from "../../../../../Server/DB/mapObjects.js";
import {ResolveObjPosInWorld, ResolveObjRotInWorld} from "../../../../../Server/DB/mapObjects/$mapObject.js";
import {AddMapObject} from "../../../../../Server/Commands/MapObjectCommands.js";
import {MaybeSnapPosChange, MaybeSnapWorldPos, RotateOffsetByRotation, TransformObjLocalPointToWorldOffset_UsingRotationOnly, TransformWorldOffsetToObjLocalPoint_UsingRotationOnly} from "../../../../../Utils/ThreeJS/SpaceUtils.js";
import {MapState} from "../../../../../Store/main/maps/@MapState.js";
import {MapSurface_current} from "../../MapSurface.js";
import {FinalizeObjNameForCloning} from "../../OutlinerUI/ObjectEntryUI_Menu.js";
import {GetAsync, observer_mgl} from "mobx-graphlink";
import {JournalPathToString} from "../../../../../Store/main/general/CopiedData.js";
import {GetDreamSceneForJournalPath} from "../../../../../Server/DB/journalEntries/$journalEntry.js";
import {GetEntity} from "../../../../../Server/DB/entities.js";
import {ignore} from "mobx-sync";

export class ToolState_AddObject {
	@O accessor shape_type: ShapeType|n = ShapeType.rectangle;
	@O accessor objPrototypeID: string|n;
	@O accessor autoRefCopiedJournalPath = true;

	@O @ignore accessor currentDrag: MapDragInfo|n;
	EndCurrentDrag() {
		if (this.currentDrag == null) return;
		RunInAction("ToolState_AddObject.EndCurrentDrag", ()=>{
			this.currentDrag = null;
		});
	}
}

export class Tool_AddObject extends Tool {
	toolType = MapTool.addObject;
	get ToolState() { return store.main.maps.tools.toolStates.addObject; }

	RenderInToolbox = observer_mgl(({data}: {data: ToolData.RenderInToolbox})=>{
		const state = this.ToolState;
		const drag = state.currentDrag;
		const currentPos = drag?.currentPos_world ?? drag?.startPos_world ?? Vec3.zero;

		const mapObjects = GetMapObjects(data.map.id);
		const prototypes = mapObjects.filter(a=>a.tags?.includes("prototype"));
		const prototypeEntries = prototypes.map(a=>({name: a.name, value: a.id}));
		// if there are entries with the same name, append the id to make them unique (else Select errors)
		for (const [i, entry] of prototypeEntries.entries()) {
			const earlierEntries = prototypeEntries.slice(0, i);
			if (earlierEntries.some(a=>a.name == entry.name)) {
				entry.name = `${entry.name} <${prototypes[i].id}>`;
			}
		}

		const journalPath = store.main.copiedData?.journalPath;
		const scene = journalPath ? GetDreamSceneForJournalPath(JournalPathToString(journalPath)) : null;
		const sceneAnchorName = scene?.sceneAnchorId ? GetEntity(scene.sceneAnchorId)?.name : null;
		const sceneTitleAndInfo =
			scene && sceneAnchorName ? `${scene.sceneTitle} [${sceneAnchorName[0].toUpperCase()}]` :
			scene ? scene.sceneTitle :
			null;
		const dragRect = drag ? this.GetCurrentDragRect(data.mapState, drag.currentPos_world) : null;
		return (
			<>
				<Select displayType="button bar" childStyle={{padding: "3px 12px"}} options={[{name: "None", value: null}, ...GetEntries(ShapeType, "ui")]} value={state.shape_type} onChange={val=>RunInAction_Set(()=>state.shape_type = val)}/>
				<Text ml={5} title="Prototype">Proto:</Text>
				<Select ml={5} options={[{name: "None", value: null}, ...prototypeEntries]} value={state.objPrototypeID} onChange={val=>RunInAction_Set(()=>state.objPrototypeID = val)}/>
				<CheckBox ml={5} text="Use journal-ref" value={state.autoRefCopiedJournalPath} onChange={val=>RunInAction_Set(()=>state.autoRefCopiedJournalPath = val)}/>
				{journalPath && <>
					{sceneTitleAndInfo != null && <Text sel ml={5} style={{fontSize: 12, opacity: .5}}>({sceneTitleAndInfo})</Text>}
					{sceneTitleAndInfo == null && <Text sel ml={5} style={{fontSize: 12, opacity: .5}}>({JournalPathToString(journalPath)})</Text>}
					<Button ml={5} mdIcon="delete" title="Clear copied journal-path" onClick={()=>RunInAction("ClearCopiedJournalPath", ()=>store.main.copiedData.journalPath = null)}/>
				</>}
				{dragRect && <Text ml={5}>{`Size: ${dragRect.sizeLocal.x.toFixed(1)}m, ${dragRect.sizeLocal.y.toFixed(1)}m`}</Text>}
			</>
		);
	});
	RenderInSceneOverlay = observer_mgl((props: {data: ToolData.RenderInSceneOverlay})=>{
		const state = this.ToolState;
		const drag = state.currentDrag;
		const mapControls_DOM = MapSurface_current?.mapControls?.DOM;
		if (drag == null || mapControls_DOM == null) return null;
		if (state.shape_type == null) return null; // if no shape-type is selected, then don't render the overlay

		const dragRect = this.GetCurrentDragRect(props.data.mapState, drag.currentPos_world);
		if (dragRect == null) return null;
		const {refPos, refRot, sizeLocal, centerWorld} = dragRect;

		const center_screen = mapControls_DOM.WorldPosToClientPos(centerWorld);
		const pixelsPerMeter = 1 / mapControls_DOM.OnePixelAsMeters().x;
		const width = sizeLocal.x * pixelsPerMeter;
		const height = sizeLocal.y * pixelsPerMeter;

		return <div style={ES(
			{
				position: "fixed",
				border: "1px solid white",
				pointerEvents: "none",
				zIndex: 100,
				left: `${center_screen.x - width / 2}px`,
				top: `${center_screen.y - height / 2}px`,
				width: `${width}px`,
				height: `${height}px`,
				transform: `rotate(${refRot}deg)`,
				transformOrigin: "center center",
			},
			state.shape_type == ShapeType.ellipse && {
				borderRadius: "50%",
			},
		)}/>;
	});

	/** Computes the drag rectangle in parent-local space for the current drag, given a current-position override.
	 * Returns null if the ref-obj pos/rot is still loading (bail). */
	GetCurrentDragRect(mapState: MapState, currentPos_world: Vec3) {
		const drag = this.ToolState.currentDrag;
		if (drag == null) return null;
		const mapID = GetOpenMapID.CatchBail(null);
		const refObj = mapID ? GetMapObject.CatchBail(undefined, mapState.referenceObject, mapID) : null;
		const refPos = refObj ? ResolveObjPosInWorld.CatchBail(undefined, refObj)?.ToVec2() : Vec2.zero;
		const refRot = refObj ? (ResolveObjRotInWorld.CatchBail(undefined, refObj) ?? 0) : 0;
		if (refPos === undefined || refRot === undefined) return null; // pos/rot still loading

		// Convert to parent-local space, then snap — this ensures local pos and size are both grid-aligned.
		const startLocal = MaybeSnapPosChange(TransformWorldOffsetToObjLocalPoint_UsingRotationOnly(drag.startPos_world.ToVec2().Minus(refPos), refRot));
		const currentLocal = MaybeSnapPosChange(TransformWorldOffsetToObjLocalPoint_UsingRotationOnly(currentPos_world.ToVec2().Minus(refPos), refRot));
		const minLocal = new Vec2(Math.min(startLocal.x, currentLocal.x), Math.min(startLocal.y, currentLocal.y));
		const maxLocal = new Vec2(Math.max(startLocal.x, currentLocal.x), Math.max(startLocal.y, currentLocal.y));
		const centerLocal = minLocal.Plus(maxLocal).Times(0.5);
		const sizeLocal = maxLocal.Minus(minLocal);
		const rectLocal = new Rect(minLocal, sizeLocal, true);
		const centerWorld = refPos.Plus(TransformObjLocalPointToWorldOffset_UsingRotationOnly(centerLocal, refRot));
		return {refPos, refRot, startLocal, currentLocal, minLocal, maxLocal, centerLocal, sizeLocal, rectLocal, centerWorld};
	}

	Document_OnKeyDown(data: ToolData.Document_OnKeyDown): ToolInterceptSubresult | void {
		if (data.event.key == "Escape") {
			this.ToolState.EndCurrentDrag();
			return {absorb: true};
		}
	}

	Scene_OnPointerDown(data: ToolData.Scene_OnPointerClick) {
		const {event, mousePos_world, controls} = data;
		const controls_DOM = controls?.DOM;
		if (event.button != 0 || controls_DOM == null) return;
		const mousePos_world_snapped = MaybeSnapWorldPos(mousePos_world.ToVec2());
		const mousePos_snapped = controls_DOM.WorldPosToClientPos(mousePos_world_snapped);

		const state = this.ToolState;
		if (state.currentDrag == null) {
			RunInAction("AddObject.Scene_OnPointerDown", ()=>{
				state.currentDrag = new MapDragInfo({
					button: event.button,
					startPos_mouse: mousePos_snapped,
					startPos_world: mousePos_world_snapped.ToVec3(0),
					currentPos_mouse: mousePos_snapped,
					currentPos_world: mousePos_world_snapped.ToVec3(0),
				});
			});
		}
	}

	Document_OnPointerMove(data: ToolData.Document_OnPointerMove) {
		const {event, mousePos, mousePos_world, controls} = data;
		const controls_DOM = controls?.DOM;
		if (controls_DOM == null) return;
		const state = this.ToolState;
		const mousePos_world_snapped = MaybeSnapWorldPos(mousePos_world.ToVec2());
		const mousePos_snapped = controls_DOM.WorldPosToClientPos(mousePos_world_snapped);

		const drag = state.currentDrag;
		if (drag) {
			RunInAction("AddObject.Document_OnPointerMove", ()=>{
				drag.currentPos_mouse = mousePos_snapped;
				drag.currentPos_world = mousePos_world_snapped.ToVec3(0);
			});
		}
	}

	Document_OnPointerUp(data: ToolData.Document_OnPointerUp) {
		const {event, mousePos_world, controls} = data;
		const state = this.ToolState;
		const drag = state.currentDrag;
		if (drag?.button != event.button) return;
		//(async()=>{

		const endPos_snapped = MaybeSnapWorldPos(mousePos_world.ToVec2());
		const mapState = GetMapState.CatchBail(null, GetOpenMapID.CatchBail(null));
		if (mapState == null) return;
		const mapID = GetOpenMapID.CatchBail(null);
		if (!mapID) return;
		const mapObjects = GetMapObjects.CatchBail(null, mapID);
		if (!mapObjects) return;

		let refObj: MapObject|n = GetMapObject.CatchBail(undefined, mapState.referenceObject, mapID);
		const dragRect = this.GetCurrentDragRect(mapState, endPos_snapped.ToVec3(0));
		if (dragRect == null) return; // ref-obj pos/rot still loading — bail, user can retry
		const {rectLocal} = dragRect;
		refObj = refObj ?? null; // keep refObj reference for parent assignment below

		//const prototypeObj = state.objPrototypeID ? GetMapObject.CatchBail(null, state.objPrototypeID) : null;
		//const prototypeObj = state.objPrototypeID ? await GetAsync(()=>GetMapObject(state.objPrototypeID)) : null;
		const prototypeObj = state.objPrototypeID ? mapObjects.find(a=>a.id == state.objPrototypeID) : null;
		const WithNullsStripped = function<T extends object>(a: T): T { return Object.fromEntries(Object.entries(a).filter(([_, v])=>v != null)) as T; };
		const base = WithNullsStripped((Clone(prototypeObj) ?? {}) as Partial<MapObject>)
			.ExcludeKeys(
				"id", "creator", "createdAt", "map", "position",
				// the prototype itself is generally hidden; new instances should be visible
				"hideSelf", "hideSubtree",
			);
		if (base.tags?.includes("prototype")) {
			base.tags = base.tags.filter(a=>a != "prototype");
			if (base.tags.length == 0) delete base.tags;
		}

		const newObject = new MapObject(E(
			// props here should be overridden by prototype, if set there
			{},
			// prototype apply
			base,
			// required overrides
			{
				map: mapID,
				parent: refObj?.id,
				name: FinalizeObjNameForCloning(
					(base?.name?.includes("<noClone>") ? null : base?.name) ??
					`New (${new Date().toLocaleTimeString("sv")})`
				),
				position: rectLocal.Center.ToVec3(),
			},
			// conditional overrides
			state.shape_type != null && {
				shape_type: state.shape_type,
				shape_size: rectLocal.Size,
			},
			// if no shape-type set, and NO label size-basis is set, then set a default label size-basis (1m font-height)
			(()=>{
				if (state.shape_type != null) return;
				const anyLabelSizeBasisSet = (
					base.label_baseWidth_world != null || base.label_baseFontHeight_world != null
					|| base.label_minWidth_screen != null || base.label_maxWidth_screen != null
					|| base.label_minHeight_screen != null || base.label_maxHeight_screen != null
				);
				if (anyLabelSizeBasisSet) return;
				return {
					label_baseFontHeight_world: 1,
				};
			})(),
		));
		if (state.autoRefCopiedJournalPath && store.main.copiedData?.journalPath) {
			newObject.journalSegmentPaths ??= [];
			newObject.journalSegmentPaths.push(JournalPathToString(store.main.copiedData.journalPath));
		}
		new AddMapObject({entry: newObject}).RunOnServer().then(response=>{
			const newObjId = response.id;
			RunInAction_Set(()=>{
				mapState.selectedObjects = [newObjId];
				mapState.activeObject = newObjId;
			});
		});

		this.ToolState.EndCurrentDrag();
		//})();
	}
}